import { Label } from "layout/typography/Label";
import { sortBy } from "lodash";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Message } from "primereact/message";
import { useEffect, useState } from "react";
import { useGetAllPayersQuery } from "store/queries/payers";
import {
  IClinicCopayRule,
  IClinicCopayRuleSaveRequest,
} from "types/ClinicCopayRule/ClinicCopayRule";

import {
  IClinicCopayDraft,
  KIND_OPTIONS,
  clinicCopayDraftProblem,
  clinicCopayToSaveRequest,
  showsAmountField,
  toClinicCopayDraft,
} from "./clinicCopayRuleWording";
import { CPT_GROUP_PRECEDENCE } from "./cptGroupWording";
import { MODALITY_LABELS } from "./serviceTypeRuleWording";

interface IClinicCopayRuleDialogProps {
  visible: boolean;
  /** The rule being edited, or null when adding. */
  rule: IClinicCopayRule | null;
  /** Every rule, for the duplicate check. */
  rules: IClinicCopayRule[];
  /** The CPT groups whose amount can be read from a benefit block. */
  blockGroups: string[];
  saving: boolean;
  onSave: (body: IClinicCopayRuleSaveRequest) => void;
  onHide: () => void;
}

const listOf = (data: any): any[] => (data as any)?.data || data || [];

const GROUP_OPTIONS = CPT_GROUP_PRECEDENCE.map((group) => ({
  label: MODALITY_LABELS[group],
  value: group,
}));

/**
 * CLE-3050 — Add / Edit one clinic copay rule. A draft held locally, checked here with the API's
 * own wording before it is sent, and the whole rule sent on save. The kind dropdown lists all
 * three kinds; only Extra copay can be picked today. The amount field shows for a CPT group with
 * no benefit block (always a fixed amount) or when "Use a fixed amount" is ticked.
 */
export function ClinicCopayRuleDialog({
  visible,
  rule,
  rules,
  blockGroups,
  saving,
  onSave,
  onHide,
}: IClinicCopayRuleDialogProps) {
  const [draft, setDraft] = useState<IClinicCopayDraft>(
    toClinicCopayDraft(rule)
  );
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setDraft(toClinicCopayDraft(rule));
      setProblem(null);
    }
  }, [visible, rule]);

  const { data: payersData } = useGetAllPayersQuery(undefined, {
    skip: !visible,
  });
  const payerOptions = sortBy(
    listOf(payersData)
      .filter((payer: any) => !payer.archivedAt || payer.id === rule?.payerId)
      .map((payer: any) => ({ label: payer.name, value: payer.id })),
    ["label"]
  );
  // A rule's payer that the payer list does not hold (yet) still shows by name.
  if (
    rule &&
    !payerOptions.some((option: any) => option.value === rule.payerId)
  ) {
    payerOptions.unshift({
      label: rule.payerName ?? `Payer ${rule.payerId}`,
      value: rule.payerId,
    });
  }

  const set = (patch: Partial<IClinicCopayDraft>) =>
    setDraft((current) => ({ ...current, ...patch }));

  const groupHasBlock =
    draft.procedureGroup !== null && blockGroups.includes(draft.procedureGroup);
  const amountShown = showsAmountField(draft, blockGroups);

  const save = () => {
    const found = clinicCopayDraftProblem(
      draft,
      rules,
      blockGroups,
      rule
        ? { payerId: rule.payerId, procedureGroup: rule.procedureGroup }
        : null
    );
    setProblem(found);
    if (found) return;
    onSave(clinicCopayToSaveRequest(draft, blockGroups));
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        type="button"
        label="Cancel"
        className="p-button-text"
        onClick={onHide}
        data-testid="clinicCopay.dialog.cancel"
      />
      <Button
        type="button"
        label={rule ? "Save rule" : "Add rule"}
        loading={saving}
        onClick={save}
        data-testid="clinicCopay.dialog.save"
      />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={rule ? "Edit clinic copay rule" : "Add a clinic copay rule"}
      footer={footer}
      style={{ width: "min(600px, 95vw)" }}
      data-testid="clinicCopay.dialog"
    >
      <div className="flex flex-column gap-3">
        <div className="flex flex-column gap-1">
          <Label htmlFor="clinicCopay.payer">Payer</Label>
          <Dropdown
            inputId="clinicCopay.payer"
            data-testid="clinicCopay.payer"
            options={payerOptions}
            value={draft.payerId}
            onChange={(event) => set({ payerId: event.value ?? null })}
            placeholder="Choose a payer"
            filter
          />
        </div>

        <div className="flex flex-column gap-1">
          <Label htmlFor="clinicCopay.group">CPT group</Label>
          <Dropdown
            inputId="clinicCopay.group"
            data-testid="clinicCopay.group"
            options={GROUP_OPTIONS}
            value={draft.procedureGroup}
            onChange={(event) => set({ procedureGroup: event.value ?? null })}
          />
        </div>

        <div className="flex flex-column gap-1">
          <Label htmlFor="clinicCopay.kind">Kind</Label>
          <Dropdown
            inputId="clinicCopay.kind"
            data-testid="clinicCopay.kind"
            options={KIND_OPTIONS}
            optionDisabled="disabled"
            value={draft.kind}
            onChange={(event) => set({ kind: event.value })}
          />
        </div>

        {groupHasBlock && (
          <div className="flex align-items-center gap-2">
            <Checkbox
              inputId="clinicCopay.useFixedAmount"
              data-testid="clinicCopay.useFixedAmount"
              checked={draft.useFixedAmount}
              onChange={(event) => set({ useFixedAmount: !!event.checked })}
            />
            <label htmlFor="clinicCopay.useFixedAmount">
              Use a fixed amount instead of the eligibility response’s value
            </label>
          </div>
        )}

        {amountShown ? (
          <div className="flex flex-column gap-1">
            <Label htmlFor="clinicCopay.amount">Fixed amount</Label>
            <InputNumber
              inputId="clinicCopay.amount"
              data-testid="clinicCopay.amount"
              value={draft.amountDollars}
              onValueChange={(event) =>
                set({ amountDollars: event.value ?? null })
              }
              mode="currency"
              currency="USD"
              locale="en-US"
              min={0}
            />
            {!groupHasBlock && (
              <small
                className="text-color-secondary"
                data-testid="clinicCopay.dialog.noBlockHelp"
              >
                This CPT group has no benefit block in the eligibility response,
                so the add-on copay is always this amount.
              </small>
            )}
          </div>
        ) : (
          <small
            className="text-color-secondary"
            data-testid="clinicCopay.dialog.blockHelp"
          >
            The amount is read from the eligibility response’s{" "}
            {draft.procedureGroup === "XRAY" ? "X-Ray" : "MRI/CAT Scan"} block.
          </small>
        )}

        <div className="flex align-items-center gap-2">
          <Checkbox
            inputId="clinicCopay.enabled"
            data-testid="clinicCopay.enabled"
            checked={draft.enabled}
            onChange={(event) => set({ enabled: !!event.checked })}
          />
          <label htmlFor="clinicCopay.enabled">Enabled</label>
        </div>

        {problem && (
          <Message
            severity="error"
            text={problem}
            data-testid="clinicCopay.dialog.problem"
          />
        )}
      </div>
    </Dialog>
  );
}
